import { Link } from "react-router-dom";
import { ArrowRight, Crown, Gem, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { SocialButtons } from "@/components/SocialButtons";
import { mediaUrl } from "@/lib/api";

const parseAmount = (s) => Number(String(s || "").replace(/[^\d]/g, "")) || 0;

const topPackage = (packages = []) =>
  packages.reduce((best, p) => (parseAmount(p.reward) > parseAmount(best?.reward) ? p : best), null);

const Collage = ({ s, top }) => {
  const { t, tr } = useLang();
  return (
    <div className="relative grid h-[420px] grid-cols-6 grid-rows-6 gap-3 sm:h-[520px]" data-testid="hero-collage">
      <img src={mediaUrl(s.hero_image_url)} alt="luxury home" data-testid="hero-image" className="col-span-6 row-span-4 h-full w-full rounded-[1.75rem] object-cover shadow-2xl shadow-black/60" />
      <img src={mediaUrl(s.hero_image_2_url)} alt="luxury car" data-testid="hero-image-2" className="col-span-3 row-span-2 h-full w-full rounded-3xl object-cover" />
      <img src={mediaUrl(s.hero_image_3_url)} alt="wealth" data-testid="hero-image-3" className="col-span-3 row-span-2 h-full w-full rounded-3xl object-cover" />
      {top && (
        <div className="animate-fade-up absolute left-4 top-4 rounded-2xl border border-amber-300/30 bg-black/55 px-5 py-4 backdrop-blur-md sm:left-6 sm:top-6" style={{ animationDelay: "400ms" }} data-testid="hero-top-reward">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-300"><Crown className="h-4 w-4" />{tr(top.name)}</p>
          <p className="mt-1 text-2xl font-extrabold text-white sm:text-3xl">{top.reward}</p>
          <p className="text-xs text-white/60">{t("reward")}</p>
        </div>
      )}
      <div className="absolute -right-3 top-[58%] hidden h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 via-amber-400 to-amber-700 text-4xl font-black text-black shadow-xl shadow-amber-500/30 sm:flex">$</div>
    </div>
  );
};

export const LuxuryHero = ({ s }) => {
  const { t, tr } = useLang();
  const top = topPackage(s.packages);
  return (
    <section className="relative overflow-hidden bg-[#07090A] text-white" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-amber-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 right-0 h-[480px] w-[480px] rounded-full bg-emerald-600/15 blur-3xl" />
      <span className="pointer-events-none absolute -bottom-24 left-6 select-none text-[22rem] font-black leading-none text-white/[0.03]">$</span>
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-24">
        <div className="lg:col-span-6">
          <span className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-amber-200">
            <Gem className="h-3.5 w-3.5" />{t("hero_badge")}
          </span>
          <h1 className="animate-fade-up mt-6 bg-gradient-to-br from-amber-100 via-amber-300 to-amber-600 bg-clip-text text-4xl font-extrabold leading-tight tracking-tight text-transparent sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }} data-testid="hero-title">
            {tr(s.hero_title)}
          </h1>
          <p className="animate-fade-up mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg" style={{ animationDelay: "160ms" }} data-testid="hero-subtitle">{tr(s.hero_subtitle)}</p>
          {top && (
            <div className="animate-fade-up mt-8 flex items-end gap-4 border-l-2 border-amber-400 pl-5" style={{ animationDelay: "220ms" }}>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-white/50">{t("rewards_upto")}</p>
                <p className="text-3xl font-extrabold text-amber-300 sm:text-4xl" data-testid="hero-reward-amount">{top.reward}</p>
              </div>
            </div>
          )}
          <div className="animate-fade-up mt-8 flex flex-wrap gap-3" style={{ animationDelay: "280ms" }}>
            <Link to="/register" data-testid="hero-register-button" className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 px-7 py-3.5 font-bold text-black shadow-lg shadow-amber-500/25 transition-transform hover:scale-[1.03]">
              {t("hero_cta_register")}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="#packages" data-testid="hero-packages-button" className="inline-flex items-center rounded-full border border-white/20 px-7 py-3.5 font-bold text-white transition-colors hover:bg-white/10">
              {t("hero_cta_packages")}
            </a>
          </div>
          <div className="animate-fade-up mt-6" style={{ animationDelay: "340ms" }}><SocialButtons variant="ghost" testPrefix="hero" /></div>
          <p className="mt-8 flex items-center gap-2 text-sm text-white/50"><ShieldCheck className="h-4 w-4 text-emerald-400" />{t("hero_trust")}</p>
        </div>
        <div className="animate-fade-up lg:col-span-6" style={{ animationDelay: "200ms" }}><Collage s={s} top={top} /></div>
      </div>
    </section>
  );
};
